/**
 * Homepage Top 10 finder, step B: carrying a visitor's selections from the
 * card into signup and on into their first deal.
 *
 * The payload is stored in localStorage under an opaque token, the token
 * travels in the signup URL, and account creation copies the payload into
 * `pending_onboarding` so it survives email verification on another device.
 */
import { DISPLAY_NAME, STAGE_LABEL } from "@/lib/ranking-display-names";
import { PROVIDER_CATEGORIES } from "@/lib/provider-rankings";

export type FinderRaiseType = "company" | "real_estate" | "fund" | "loan";

export type FinderBranch = "DirectEquityMezz" | "RealEstate" | "Fund" | "Loans/Debt";

export type FinderAnswers = {
  company_stage?: string;
  company_sector?: string;
  re_capital_type?: string;
  property_type?: string;
  fund_profile?: string;
  fund_strategy?: string;
  loan_type?: string;
};

/**
 * A firm from the Top 10 list the visitor was looking at. Carried through
 * signup so the firms they saw become the deal's included list, which is
 * what makes the first delivery contain the firms they came for.
 */
export type FinderFirm = { name: string; domain: string };

export type FinderHandoff = {
  raise_type: FinderRaiseType;
  answers: FinderAnswers;
  /** The ten firms shown on the list, name plus domain where known. */
  firms?: FinderFirm[];
  list_key: { groupKey: string | null; categoryKey: string | null };
  /** Human label of the list that was shown, used in the dashboard line. */
  list_label: string;
  captured_at: string;
};

export const ANSWER_KEYS = [
  "company_stage",
  "company_sector",
  "re_capital_type",
  "property_type",
  "fund_profile",
  "fund_strategy",
  "loan_type",
] as const;

const RAISE_TYPES: FinderRaiseType[] = ["company", "real_estate", "fund", "loan"];

/** The card ids used on the homepage map onto the stored raise types. */
export function normalizeRaiseType(value: unknown): FinderRaiseType | null {
  if (typeof value !== "string") return null;
  const v = value.trim();
  if (v === "business_loan" || v === "loans" || v === "loan") return "loan";
  return (RAISE_TYPES as string[]).includes(v) ? (v as FinderRaiseType) : null;
}

export function branchForRaiseType(raiseType: FinderRaiseType): FinderBranch {
  switch (raiseType) {
    case "company":
      return "DirectEquityMezz";
    case "real_estate":
      return "RealEstate";
    case "fund":
      return "Fund";
    case "loan":
      return "Loans/Debt";
  }
}

export function providerCategoryLabel(
  groupKey: string | null,
  categoryKey: string | null,
): string | null {
  if (!groupKey || !categoryKey) return null;
  const hit = PROVIDER_CATEGORIES.find(
    (c) => c.groupKey === groupKey && c.categoryKey === categoryKey,
  );
  return hit ? hit.label : null;
}

const FUND_PROFILE_LABEL: Record<string, string> = {
  funds_established: "Established manager",
  funds_emerging: "Emerging manager",
  funds_seeder: "Seeking a seeder",
};

/** Build the payload stored at the moment the visitor clicks the trial button. */
export function buildHandoff(
  cardId: string,
  answers: FinderAnswers,
  now: Date = new Date(),
  firms: FinderFirm[] = [],
): FinderHandoff | null {
  const raiseType = normalizeRaiseType(cardId);
  if (!raiseType) return null;
  let groupKey: string | null = null;
  let categoryKey: string | null = null;
  let label = "";
  if (raiseType === "company") {
    categoryKey = answers.company_sector ?? null;
    const sector = categoryKey ? (DISPLAY_NAME[categoryKey] ?? categoryKey) : "";
    const stage = answers.company_stage ? (STAGE_LABEL[answers.company_stage] ?? answers.company_stage) : "";
    label = [stage, sector].filter(Boolean).join(" investors in ");
  } else if (raiseType === "real_estate") {
    groupKey = answers.re_capital_type === "debt" ? "re_debt" : "re_equity";
    categoryKey = answers.property_type ?? null;
    const cat = providerCategoryLabel(groupKey, categoryKey) ?? categoryKey ?? "";
    label = `${cat} ${answers.re_capital_type === "debt" ? "debt" : "equity"}`.trim();
  } else if (raiseType === "fund") {
    groupKey = answers.fund_profile ?? null;
    categoryKey = answers.fund_strategy ?? null;
    const cat = providerCategoryLabel(groupKey, categoryKey) ?? categoryKey ?? "";
    const profile = groupKey ? (FUND_PROFILE_LABEL[groupKey] ?? groupKey) : "";
    label = [cat, profile].filter(Boolean).join(", ");
  } else {
    groupKey = "loans";
    categoryKey = answers.loan_type ?? null;
    label = providerCategoryLabel(groupKey, categoryKey) ?? categoryKey ?? "";
  }
  const cleaned: FinderAnswers = {};
  for (const key of ANSWER_KEYS) {
    const value = answers[key];
    if (typeof value === "string" && value.trim()) cleaned[key] = value.trim().slice(0, 120);
  }
  return {
    raise_type: raiseType,
    answers: cleaned,
    firms: cleanFirms(firms),
    list_key: { groupKey, categoryKey },
    list_label: label.slice(0, 160),
    captured_at: now.toISOString(),
  };
}

/**
 * Normalize and bound the firm list on both write and read. Ten rows is
 * the whole list, so anything longer is untrusted input and is cut.
 */
export function cleanFirms(value: unknown): FinderFirm[] {
  if (!Array.isArray(value)) return [];
  const out: FinderFirm[] = [];
  const seen = new Set<string>();
  for (const item of value.slice(0, 20)) {
    if (!item || typeof item !== "object") continue;
    const rec = item as Record<string, unknown>;
    const name = typeof rec.name === "string" ? rec.name.trim().slice(0, 160) : "";
    const domainRaw = typeof rec.domain === "string" ? rec.domain : "";
    const domain = domainRaw
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, "")
      .replace(/^www\./, "")
      .replace(/[/?#].*$/, "")
      .slice(0, 253);
    if (!name && !domain) continue;
    const key = domain || name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name, domain });
    if (out.length >= 10) break;
  }
  return out;
}

/** Strict validation for anything crossing a trust boundary (URL, metadata, database). */
export function parseHandoff(value: unknown): FinderHandoff | null {
  if (!value || typeof value !== "object") return null;
  const raw = value as Record<string, unknown>;
  const raiseType = normalizeRaiseType(raw.raise_type);
  if (!raiseType) return null;
  const answersRaw = (raw.answers ?? {}) as Record<string, unknown>;
  const answers: FinderAnswers = {};
  for (const key of ANSWER_KEYS) {
    const v = answersRaw[key];
    if (typeof v === "string" && v.trim()) answers[key] = v.trim().slice(0, 120);
  }
  const keyRaw = (raw.list_key ?? {}) as Record<string, unknown>;
  const groupKey = typeof keyRaw.groupKey === "string" ? keyRaw.groupKey.slice(0, 64) : null;
  const categoryKey =
    typeof keyRaw.categoryKey === "string" ? keyRaw.categoryKey.slice(0, 120) : null;
  const label = typeof raw.list_label === "string" ? raw.list_label.slice(0, 160) : "";
  const capturedAt =
    typeof raw.captured_at === "string" && !Number.isNaN(Date.parse(raw.captured_at))
      ? raw.captured_at
      : new Date(0).toISOString();
  return {
    raise_type: raiseType,
    answers,
    firms: cleanFirms(raw.firms),
    list_key: { groupKey, categoryKey },
    list_label: label,
    captured_at: capturedAt,
  };
}

/** Description sentences seeded into the deal builder so nothing is retyped. */
export function handoffSentences(handoff: FinderHandoff): string {
  const a = handoff.answers;
  const out: string[] = [];
  switch (handoff.raise_type) {
    case "company": {
      if (a.company_sector) out.push(`Sector: ${DISPLAY_NAME[a.company_sector] ?? a.company_sector}.`);
      if (a.company_stage) out.push(`Stage: ${STAGE_LABEL[a.company_stage] ?? a.company_stage}.`);
      out.push("This is a company raise.");
      break;
    }
    case "real_estate": {
      const prop = providerCategoryLabel(handoff.list_key.groupKey, handoff.list_key.categoryKey);
      if (prop) out.push(`Property type: ${prop}.`);
      out.push(
        a.re_capital_type === "debt"
          ? "This is a real estate debt raise."
          : "This is a real estate equity raise.",
      );
      break;
    }
    case "fund": {
      const strategy = providerCategoryLabel(handoff.list_key.groupKey, handoff.list_key.categoryKey);
      if (strategy) out.push(`Fund strategy: ${strategy}.`);
      if (a.fund_profile) out.push(`Manager profile: ${FUND_PROFILE_LABEL[a.fund_profile] ?? a.fund_profile}.`);
      out.push("This is a fund raising limited partner commitments.");
      break;
    }
    case "loan": {
      const loan = providerCategoryLabel("loans", handoff.list_key.categoryKey);
      if (loan) out.push(`Financing type: ${loan}.`);
      out.push("This is a business borrowing request.");
      break;
    }
  }
  return out.join(" ");
}

/** One line shown on the first dashboard visit. */
export function dashboardLine(handoff: FinderHandoff): string {
  const label = handoff.list_label || "your selections";
  return `We kept your Top 10 search (${label}). Start a deal to develop it.`;
}

/* ---------------------------------------------------------------- */
/* Browser storage                                                   */
/* ---------------------------------------------------------------- */

export const HANDOFF_PREFIX = "cd_finder_handoff_";

export function makeHandoffToken(): string {
  const cryptoObj = typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return cryptoObj.randomUUID().replace(/-/g, "").slice(0, 24);
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export function saveHandoff(handoff: FinderHandoff): string | null {
  const token = makeHandoffToken();
  try {
    if (typeof window === "undefined") return null;
    window.localStorage.setItem(HANDOFF_PREFIX + token, JSON.stringify(handoff));
    return token;
  } catch {
    return null;
  }
}

export function readHandoff(token: string | undefined | null): FinderHandoff | null {
  if (!token) return null;
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(HANDOFF_PREFIX + token);
    if (!raw) return null;
    return parseHandoff(JSON.parse(raw));
  } catch {
    return null;
  }
}

export function clearHandoff(token: string | undefined | null): void {
  if (!token) return;
  try {
    if (typeof window === "undefined") return;
    window.localStorage.removeItem(HANDOFF_PREFIX + token);
  } catch {
    // storage is best effort
  }
}
